import { useState, useEffect, useCallback } from "react";
import { onAuthStateChanged } from "firebase/auth";
import dayjs from "dayjs";

import authService from "../service/auth.service";
import { auth } from "../utils/auth/firebase";
import { updateUserOnLogout } from "../utils/auth/updateUserOnLogout";
import IdleTimer from "../utils/auth/IdleTimer";

// ✅ Central config
const IDLE_TIMEOUT = 2700; // seconds (2700 = 45min)

export default function useAuth() {
  // ✅ State for user + UI
  const [currentUser, setCurrentUser] = useState(undefined);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  const [loading, setLoading] = useState(true);
  const [referenceDate, setReferenceDate] = useState(undefined);
  const [currentVerificationUser, setCurrentVerificationUser] =
    useState(undefined);

  // ✅ Listen to Firebase auth state (login/logout)
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentVerificationUser(user);
    });

    // Cleanup prevents memory leaks
    return unsubscribe;
  }, []);

  // ✅ Load user data from your backend once
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const user = await authService.getCurrentUser();
        if (user) setCurrentUser(user);
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
  }, []);

  // ✅ Logout logic (memoized so it doesn't recreate on every render)
  const logout = useCallback(
    async (trigger) => {
      setLoggingOut(true);

      const expiredTime = Number(localStorage.getItem("_expiredTime"));

      // ✅ Only update user if session ended normally (not expired)
      if (
        trigger !== "expired" &&
        trigger !== "timeout" &&
        expiredTime > 0 &&
        currentUser
      ) {
        await updateUserOnLogout(dayjs(), currentUser);
      }

      // ✅ Small delay for smoother UX (optional)
      setTimeout(async () => {
        await authService.clearStorage();
        setCurrentUser(undefined);

        setTimeout(() => setLoggingOut(false), 300);
      }, 1000);
    },
    [currentUser],
  );

  // ✅ Handle authentication state + idle timeout
  useEffect(() => {
    if (!currentUser) {
      setIsAuthenticated(false);
      return;
    }

    setIsAuthenticated(true);

    // ✅ Store "login date" (e.g. for session tracking)
    setReferenceDate(dayjs().format("YYYY-MM-DD"));

    // ✅ Start idle timer → auto logout after inactivity
    const timer = new IdleTimer({
      timeout: IDLE_TIMEOUT,
      onTimeout: () => logout("timeout"),
      onExpired: () => logout("expired"),
    });

    // Cleanup timer on unmount or user change
    return () => timer.cleanUp();
  }, [currentUser, logout]);

  // ✅ Public API of the hook
  return {
    currentUser,
    setCurrentUser,
    isAuthenticated,
    loading,
    loggingOut,
    referenceDate,
    currentVerificationUser,
    logout,
  };
}
